import {
  infraAddDays,
  type InfraBillingKind,
  type InfraCadence,
  type InfraCurrency,
  type InfraScheduleItem,
} from '@carsfinders/contracts';

import { t } from '../../lib/i18n';

/**
 * Display helpers for «Інфраструктура». The wire carries raw values only
 * (amounts as numbers in their own currency, `YYYY-MM-DD` dates, enum keys);
 * everything an operator reads is produced here or in the catalogue.
 *
 * Every helper that interpolates passes a NON-EMPTY string: the admin `t()`
 * leaves the raw `{token}` in the text when a parameter is falsy.
 */

/** Currencies whose large amounts are printed without cents. */
const WHOLE_WHEN_LARGE: ReadonlySet<InfraCurrency> = new Set(['USD', 'EUR', 'CHF']);

/** «1 234 $», «12,50 €», «2 450,00 грн» — `uk-UA`, 0 digits for USD/EUR/CHF ≥ 100, else 2. */
export function formatMoney(amount: number, currency: InfraCurrency): string {
  const digits = WHOLE_WHEN_LARGE.has(currency) && Math.abs(amount) >= 100 ? 0 : 2;
  return new Intl.NumberFormat('uk-UA', {
    style: 'currency',
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(amount);
}

export function formatUsd(amount: number): string {
  return formatMoney(amount, 'USD');
}

/** `≈ ` in front of a figure that contains an estimate. */
export function approx(text: string, isEstimate: boolean): string {
  return isEstimate ? `≈ ${text}` : text;
}

/** `2026-10-14` → «14.10.2026»; anything unparseable comes back unchanged. */
export function formatDate(date: string | null | undefined): string {
  if (!date) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(date);
  return match ? `${match[3]}.${match[2]}.${match[1]}` : date;
}

/** The history window: the last 365 days up to `today`. */
export function historyRange(today: string): { from: string; to: string } {
  return { from: infraAddDays(today, -365), to: today };
}

/** «прострочено 3 дн.» / «сьогодні» / «через 5 дн.». */
export function scheduleStatusLabel(item: Pick<InfraScheduleItem, 'status' | 'daysUntil'>): string {
  if (item.status === 'overdue') {
    return t('admin.infraCosts.status.overdue', { days: String(Math.max(1, -item.daysUntil)) });
  }
  if (item.daysUntil === 0) return t('admin.infraCosts.status.today');
  return t('admin.infraCosts.status.inDays', { days: String(item.daysUntil) });
}

/** «Щомісяця» / «Передоплата · щокварталу» / «Разово». */
export function billingLabel(kind: InfraBillingKind, cadence: InfraCadence | null | undefined): string {
  if (kind === 'one_off') return t('admin.infraCosts.billingKind.one_off');
  const cadenceText = cadence ? t(`admin.infraCosts.cadence.${cadence}`) : null;
  if (kind === 'recurring') return cadenceText ?? t('admin.infraCosts.billingKind.recurring');
  return cadenceText
    ? `${t('admin.infraCosts.billingKind.prepaid')} · ${cadenceText}`
    : t('admin.infraCosts.billingKind.prepaid');
}

/** The amount in its own currency, plus «≈ $X» when it is not already USD. */
export function amountWithUsd(item: Pick<InfraScheduleItem, 'amount' | 'currency' | 'amountUsd' | 'amountIsEstimate'>): {
  own: string;
  usd: string | null;
} {
  return {
    own: approx(formatMoney(item.amount, item.currency), item.amountIsEstimate),
    usd: item.currency === 'USD' ? null : `≈ ${formatUsd(item.amountUsd)}`,
  };
}
